import type { TenantRow } from '../schemas/bulk-migration';

/**
 * A dry-run row's records not copied by design (INI-0035 follow-ups D1), as the
 * analysis shows them under the client's outcome: "412 not copied by design",
 * then one line per reason code with its count.
 *
 * The runner counts them over the WHOLE discovery (`notMigratedByDesign`, and
 * `notMigratedByCode`: code → count, codes and counts only), not only the
 * records the row keeps, so this is the number QA reconciles against. Absent
 * (an older runner, or a row with none) and `0` show nothing.
 */

/** Veevart copy for the reason codes this screen knows; any other code reads as itself. */
const CODE_COPY: Readonly<Record<string, string>> = {
    CONSTITUENTS_HOUSEHOLDS_NO_MEMBERS: 'Households with no members in Salesforce',
    CONSTITUENTS_SUPPLIERS_HOUSEHOLD: 'Vendors that are households in Salesforce',
};

export interface NotMigratedByDesignLine {
    readonly code: string;
    readonly count: number;
    /** The Veevart copy for a known code, or `null` (the code is shown on its own). */
    readonly label: string | null;
}

export interface NotMigratedByDesignSummary {
    readonly total: number;
    /** "412 not copied by design". */
    readonly headline: string;
    /** Largest first, then by code. */
    readonly lines: readonly NotMigratedByDesignLine[];
}

export function notMigratedByDesignSummary(
    row: Pick<TenantRow, 'notMigratedByDesign' | 'notMigratedByCode'>,
): NotMigratedByDesignSummary | null {
    const total = row.notMigratedByDesign ?? 0;
    if (total <= 0) return null;

    const lines = Object.entries(row.notMigratedByCode ?? {})
        .filter(([, count]) => count > 0)
        .sort(([a, x], [b, y]) => y - x || a.localeCompare(b))
        .map(([code, count]) => ({ code, count, label: CODE_COPY[code] ?? null }));

    return { total, headline: `${total.toLocaleString('en-US')} not copied by design`, lines };
}

/** One breakdown line: "412 · Households with no members in Salesforce (CODE)", or "3 · CODE". */
export function notMigratedByDesignLine(line: NotMigratedByDesignLine): string {
    const count = line.count.toLocaleString('en-US');
    return line.label === null
        ? `${count} · ${line.code}`
        : `${count} · ${line.label} (${line.code})`;
}
